import React from 'react';
import { usePriceChange } from '@/hooks/usePriceChange';

interface PriceChangeDisplayProps {
    instrumentId: string;
    currentPrice: number;
    className?: string;
    /**
     * Whether to fetch chart history to calculate 24h change.
     * Set to false on list/market pages to avoid unnecessary API calls.
     * Set to true on detail pages where 24h change is important.
     * @default false
     */
    show24hChange?: boolean;
}

const PriceChangeDisplay: React.FC<PriceChangeDisplayProps> = ({
    instrumentId,
    currentPrice,
    className = '',
    show24hChange = false,
}) => {
    const { data: priceChangeData, isLoading } = usePriceChange(
        instrumentId,
        currentPrice,
        show24hChange
    );

    // If 24h change is disabled, don't show anything (or show a placeholder)
    if (!show24hChange) {
        return null;
    }

    if (isLoading || !priceChangeData) {
        return <span className={className}>--</span>;
    }

    const changePercent = priceChangeData.changePercent || 0;
    const isPositive = changePercent >= 0;

    return (
        <span className={`${className} ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
            {isPositive ? '+' : ''}
            {changePercent.toFixed(2)}%
        </span>
    );
};

export default PriceChangeDisplay;
